/**
 * @file Feeding calculation functions.
 * @fileoverview Uses Vue3 cdn mainly composition api.
 *
 * Creates a calculator to help provide product recommendations
 * based on the user's input
 *
 * Variable names
 * If a variable name has a underscore it is most likely text
 * If a variable name starts with `field` it is most likely the selected data from input
 * If a variable name starts with `t` it is most likely related to the translation data
 */

/**
 * Represents a product variant.
 *  * https://shopify.dev/docs/api/storefront/unstable/objects/ProductVariant
 * @typedef {Object} ProductVariant
 * @property {boolean} availableForSale - Indicates if the product variant is available for sale.
 * @property {string} [barcode] - The barcode (for example, ISBN, UPC, or GTIN) associated with the variant.
 * @property {MoneyV2} [compareAtPrice] - The compare at price of the variant. This can be used to mark a variant as on sale, when compareAtPrice is higher than price.
 * @property {boolean} currentlyNotInStock - Whether a product is out of stock but still available for purchase (used for backorders).
 * @property {string} id - A globally-unique ID.
 * @property {Image} [image] - Image associated with the product variant. This field falls back to the product image if no image is available.
 * @property {Metafield} [metafield] - Returns a metafield found by namespace and key.
 * @property {Metafield[]} metafields - The metafields associated with the resource matching the supplied list of namespaces and keys.
 * @property {MoneyV2} price - The product variant’s price.
 * @property {ProductNode} product - The product object that the product variant belongs to.
 * @property {number} [quantityAvailable] - The total sellable quantity of the variant for online sales channels.
 * @property {QuantityRule} quantityRule - The quantity rule for the product variant in a given context.
 * @property {boolean} requiresShipping - Whether a customer needs to provide a shipping address when placing an order for the product variant.
 * @property {SelectedOption[]} selectedOptions - List of product options applied to the variant.
 * @property {string} [sku] - The SKU (stock keeping unit) associated with the variant.
 * @property {boolean} taxable - Whether tax is charged when the product variant is sold.
 * @property {string} title - The product variant’s title.
 * @property {MoneyV2} [unitPrice] - The unit price value for the variant based on the variant's measurement.
 * @property {UnitPriceMeasurement} [unitPriceMeasurement] - The unit price measurement for the variant.
 * @property {number} [weight] - The weight of the product variant in the unit system specified with weightUnit.
 * @property {WeightUnit} weightUnit - Unit of measurement for weight.
 */

/**
 * Represents a product in the Shopify store.
 * https://shopify.dev/docs/api/storefront/unstable/objects/Product
 *
 * @typedef {Object} ProductNode
 * @property {boolean} availableForSale - Indicates if at least one product variant is available for sale.
 * @property {ProductPriceRange} compareAtPriceRange - The compare at price of the product across all variants.
 * @property {Date} createdAt - The date and time when the product was created.
 * @property {Collections} collections
 * @property {string} description - Stripped description of the product, single line with HTML tags removed.
 * @property {string} descriptionHtml - The description of the product, complete with HTML formatting.
 * @property {Image} featuredImage - The featured image for the product.
 * @property {[GateSubject!]} gates - Returns a list of GateSubjects bound to the subject.
 * @property {string} handle - A human-friendly unique string for the Product automatically generated from its title.
 * @property {ID!} id - A globally-unique ID.
 * @property {boolean} isGiftCard - Whether the product is a gift card.
 * @property {Metafield} metafield - Returns a metafield found by namespace and key.
 * @property {[Metafield]!} metafields - The metafields associated with the resource matching the supplied list of namespaces and keys.
 * @property {URL} onlineStoreUrl - The URL used for viewing the resource on the shop's Online Store. Returns null if the resource is currently not published to the Online Store sales channel.
 * @property {[ProductOption!]} options - List of product options.
 * @property {ProductPriceRange} priceRange - The price range.
 * @property {string} productType - A categorization that a product can be tagged with, commonly used for filtering and searching.
 * @property {Date} publishedAt - The date and time when the product was published to the channel.
 * @property {boolean} requiresSellingPlan - Whether the product can only be purchased with a selling plan.
 * @property {SEO!} seo - The product's SEO information.
 * @property {[string!]} tags - A comma-separated list of tags that have been added to the product.
 * @property {string} title - The product’s title.
 * @property {Int} totalInventory - The total quantity of inventory in stock for this Product.
 * @property {string} trackingParameters - URL parameters to be added to a page URL when it is linked from a GraphQL result. This allows for tracking the origin of the traffic.
 * @property {Date} updatedAt - The date and time when the product was last modified. A product's updatedAt value can change for different reasons.
 * @property {ProductVariant} variantBySelectedOptions - Find a product’s variant based on its selected options. If there is not a variant for the selected options, null will be returned.
 * @property {string} vendor - The product’s vendor name.
 * @property {VariantNodes} variants
 */

/**
 * @typedef {Object} CollectionNode
 * @property {string} title
 * @property {Metafield[]} metafields
 */

/**
 * @typedef {Object} Collections
 * @property {CollectionNode} nodes
 */

/**
 * @typedef {Object} Metafield
 * @property {string} key
 * @property {string} id
 * @property {string} namespace
 * @property {string} type
 * @property {string} value
 */

/**
 * @typedef {VariantNodes}
 * @property {ProductVariant[]} nodes
 */

/**
 * @typedef {Object} ProductEdge
 * @property {ProductNode} node
 * */

/**
 * @typedef {Object} SelectedOption
 * @property {string} name Option name
 * @property {string} value The selected value for the option
 */

/**
 * @typedef {SelectedOption[] | null } SelectedOptions
 */

/**
 * @typedef {Object} FieldPortion
 * @property {boolean} addPortion
 * @property {ProductNode | null} product
 * @property {SelectedOptions | null} selectedOptions
 * @property {ProductVariant | null} selectedVariant
 * @property {number | null} percentage
 * @property {number} position
 * @property {boolean} inPreCart
 */

/**
 * @typedef {Object} PetActivity
 * @property {string} id
 * @property {string} text
 * @property {number} energy
 * @property {number} multiplier
 */
const { createApp, ref, onMounted, mounted, onBeforeMount } = Vue;

/**
 * @type {FieldPortion}
 */
const fieldPortionDefaults = {
  addPortion: false,
  product: null,
  selectedOptions: null,
  selectedVariant: null,
  percentage: null,
  position: 0,
  inPreCart: true,
};

const petPower = {
  dog: 0.75,
  cat: 0.67,
};

const isDevelopment = true;

const app = createApp({
  delimiters: ["${", "}"],
  mixins: [customMixin()],
  setup() {
    let translationData = ref(null);
    let tActivityDogBabyArr = ref(null);
    let tActivityDogAdultArr = ref(null);
    let tActivityDogSeniorArr = ref(null);
    let tActivityCatBabyArr = ref(null);
    let tActivityCatAdultArr = ref(null);
    let tActivityCatSeniorArr = ref(null);
    let tPetStageArr = ref(null);
    let calculatorSettings = ref(null);
    let fcInner = ref(null);

    onBeforeMount(() => {
      const tData = getTranslationData();
      translationData.value = tData.all;
      tPetStageArr.value = tData.petStage;
      tActivityDogBabyArr.value = tData.activityDogBaby;
      tActivityDogAdultArr.value = tData.activityDogAdult;
      tActivityDogSeniorArr.value = tData.activityDogSenior;
      tActivityCatBabyArr.value = tData.activityCatBaby;
      tActivityCatAdultArr.value = tData.activityCatAdult;
      tActivityCatSeniorArr.value = tData.activityCatSenior;
      calculatorSettings.value = getCalculatorSettings();
    });

    onMounted(() => {
      setTimeout(() => {
        fcInner.value.setAttribute("data-is-mounted", "true");
      }, 500);
    });

    return {
      addPortion: ref(null),
      calculatorProducts: ref(),
      calculatorSettings,
      controlsStep1: ref(),
      controlsStep2: ref(),
      controlsStep3: ref(),
      currentStep: ref(1),
      currencySymbol: window.storeCurrency.symbol,
      fcControls1: ref(null),
      fcInner,
      fieldPetActivity: ref({ id: null, text: null }),
      fieldPetKind: ref("dog"),
      fieldPetName: ref(""),
      fieldPetStage: ref({ stageId: null, text: null }),
      fieldPetWeight: ref(null),
      fieldPortion1: ref({
        ...fieldPortionDefaults,
        position: 1,
        percentage: 70,
      }),
      fieldPortion2: ref({
        ...fieldPortionDefaults,
        position: 2,
        percentage: 20,
      }),
      fieldPortion3: ref({
        ...fieldPortionDefaults,
        position: 3,
        percentage: 10,
      }),
      fieldPortion4: ref({
        ...fieldPortionDefaults,
        addPortion: true,
        percentage: 0,
        position: 4,
      }),
      kcal_difference: ref(0),
      kcal_per_day: ref(0),
      loadingCart: ref(false),
      numberOfServings: ref(2),
      nextButton: ref(null),
      petPower: ref(petPower.dog),
      pet_baby: ref("Puppy"),
      pet_kind: ref("dog"),
      pet_kind_possessive: ref("dog's"),
      pet_kind_pural: ref("dogs"),
      pet_name: ref(null),
      pet_stage: ref(null),
      percentageErrorText: ref(null),
      prevButton: ref(null),
      productsPortion1: ref(),
      productsPortion2: ref(),
      productsPortion3: ref(),
      tActivityCatAdultArr,
      tActivityCatBabyArr,
      tActivityCatSeniorArr,
      tActivityDogAdultArr,
      tActivityDogBabyArr,
      tActivityDogSeniorArr,
      tPetStageArr,
      totalBowlPercentage: 0,
      openPreCartProducts: ref(false),
      translationData,
      userChangedPercentage: ref(false),
      validationSteps: ref({ step1: false, step2: false, step3: false }),
      weightUnit: ref("kg"),
    };
  },
  watch: {
    fieldPetKind(newValue, oldValue) {
      this.updatePetKind(newValue);
      if (newValue != oldValue) {
        this.calculatorProducts = null;
        this.resetPetStageAndActivity();
      }
      if (newValue == "cat") {
        this.petPower = petPower.cat;
      }
      if (newValue == "dog") {
        this.petPower = petPower.dog;
      }
    },
    fieldPetName(newValue) {
      this.pet_name = newValue;
    },
    fieldPetStage() {
      this.fieldPetActivity = { id: null, text: null };
      this.validate();

      if (this.fieldPetStage.stageId === "pet_stage_senior") {
        this.fieldPetActivity = {
          id: "pet_activity_dog_senior_neither",
          text: "Neither pregnant nor lactating",
          energy: 85,
          multiplier: 0,
        };
      }
    },
    fieldPetWeight() {
      this.validate();
    },
    fieldPetActivity() {
      this.validate();
    },
    "fieldPortion1.percentage": function () {
      this.userUpdatePercentagePortion();
    },
    "fieldPortion2.percentage": function () {
      this.userUpdatePercentagePortion();
    },
    "fieldPortion3.percentage": function () {
      this.userUpdatePercentagePortion();
    },
    "fieldPortion1.addPortion": function () {
      this.userUpdatePercentagePortion();
    },
    "fieldPortion2.addPortion": function () {
      this.userUpdatePercentagePortion();
    },
    "fieldPortion3.addPortion": function () {
      this.userUpdatePercentagePortion();
    },
    async currentStep(newValue) {
      this.fcInner && autoScrollElementTop(this.fcInner, 100);
      this.$nextTick(() => {
        this.validate(newValue);
        if (newValue === 2) {
          this.gtmDataLayerForEachStep();
          this.initStep2();
        }

        if (newValue === 3) {
          this.gtmDataLayerForEachStep(2);
          this.initStep3();
        }
      });
    },
  },
  methods: {
    /**
     *
     * @param {1 | 2 | 3} doStep
     */
    gtmDataLayerForEachStep(doStep = 1) {
      window.dataLayer = window.dataLayer || [];
      const step1Fields = {
        petKind: this.fieldPetKind,
        petWeight: this.fieldPetWeight,
        petName: this.fieldPetName,
        petUnits: this.weightUnit,
        lifeStage: this.fieldPetStage.stageText,
        activityLevel: this.fieldPetActivity.text,
      };

      /**
       *
       * @param {1 | 2 | 3 | null} portionNumber
       * @param {1 | 2} servingsPerDay
       * @returns
       */
      const makeServingString = (portionNumber = null, servingsPerDay = 1) => {
        if (!portionNumber) return "";
        const gramString = `(${(
          this.calculateCupsAndCans(portionNumber).requiredGrams /
          servingsPerDay
        ).toFixed(0)}g)`;
        const ozString = `(${(
          this.calculateCupsAndCans(portionNumber).requiredOunces /
          servingsPerDay
        ).toFixed(0)}oz)`;
        const cupString = `${
          this.calculateCupsAndCans(portionNumber).cups / servingsPerDay
        } cups`;
        const canString = `${
          this.calculateCupsAndCans(portionNumber).cans / servingsPerDay
        } cans`;

        if (!this.isCan(1)) {
          return `${cupString} ${
            this.weightUnit === "kg" ? gramString : ozString
          }`;
        }
        return `${canString} ${
          this.weightUnit === "kg" ? gramString : ozString
        }`;
      };

      const portionDetailsForDataLayer = (portionNumber = 1) => {
        let layer = {
          name: "",
          percentage: 0,
          variantTitle: "",
          serving: "",
          kCal: 0,
        };

        const portion = this[`fieldPortion${portionNumber}`];
        if (!portion.addPortion) return { ...layer };

        layer.name = this.productTitle(portion.product);
        layer.percentage =
          typeof portion.percentage === "number" && portion.percentage / 100;
        layer.variantTitle = portion.selectedVariant.title;
        layer.serving = makeServingString(portionNumber);
        layer.kCal = (
          ((portion.percentage / 100) * this.kcal_per_day) /
          1
        ).toFixed(0);

        return { ...layer };
      };

      if (doStep === 1) {
        window.dataLayer.push({
          event: "bybStart",
          ...step1Fields,
        });
        return;
      }

      if (doStep === 2) {
        window.dataLayer.push({
          event: "bybData",
          base: portionDetailsForDataLayer(1).name,
          basePercentage: portionDetailsForDataLayer(1).percentage,
          basePackSize: portionDetailsForDataLayer(1).variantTitle,
          baseDailyServing: portionDetailsForDataLayer(1).serving,
          baseDailyKcal: portionDetailsForDataLayer(1).kCal,
          mixer: portionDetailsForDataLayer(2).name,
          mixerPercentage: portionDetailsForDataLayer(2).percentage,
          mixerPackSize: portionDetailsForDataLayer(2).variantTitle,
          mixerDailyServing: portionDetailsForDataLayer(2).serving,
          mixerDailyKcal: portionDetailsForDataLayer(2).kCal,
          topper: portionDetailsForDataLayer(3).name,
          topperPercentage: portionDetailsForDataLayer(3).percentage,
          topperPackSize: portionDetailsForDataLayer(3).variantTitle,
          topperDailyServing: portionDetailsForDataLayer(3).serving,
          topperDailyKcal: portionDetailsForDataLayer(3).kCal,
          recommendedDailyKcal: this.kcal_per_day,
        });
        return;
      }
      return;
    },
    /**
     *
     * @param {FieldPortion | null} portion
     */
    kCalForAPortion(portion, servings = 1) {
      if (portion) return null;
      const kcal = (
        ((portion.percentage / 100) * this.kcal_per_day) /
        servings
      ).toFixed(0);
      return kcal;
    },
    resetPetStageAndActivity() {
      this.fieldPetStage = { stageId: null, text: null };
      this.fieldPetActivity = { id: null, text: null };
    },
    makeLowerCase(str) {
      return str.toLowerCase();
    },
    /**
     * @param {FieldPortion[]} fieldPortions
     * @return {string | null}
     */
    organiseDisclaimers(fieldPortions) {
      const disclaimers = fieldPortions.map((fieldPortion) =>
        this.getProductCollectionDisclaimer(fieldPortion)
      );
      const validDisclaimers = disclaimers.filter(
        (disclaimers) =>
          disclaimers !== null &&
          disclaimers !== undefined &&
          disclaimers !== false
      );
      const uniqueDisclaimers = new Set(validDisclaimers);
      const uniqueDisclaimersArr = Array.from(uniqueDisclaimers);

      if (uniqueDisclaimersArr.length <= 0) return null;

      return uniqueDisclaimersArr;
    },

    /**
     * @param {FieldPortion[]} fieldPortions
     * @return {string | null}
     */
    organiseProductPuppyDisclaimers(fieldPortions) {
      const disclaimers = fieldPortions.map((fieldPortion) => {
        if (fieldPortion.hasOwnProperty("product") && fieldPortion.product)
          return this.getMetafieldValueByName(
            fieldPortion.product.metafields,
            "calculator_puppy_disclaimer_note"
          );
        return null;
      });
      const validDisclaimers = disclaimers.filter(
        (disclaimers) =>
          disclaimers !== null &&
          disclaimers !== undefined &&
          disclaimers !== false
      );
      const uniqueDisclaimers = new Set(validDisclaimers);
      const uniqueDisclaimersArr = Array.from(uniqueDisclaimers);

      console.log({
        disclaimers,
        validDisclaimers,
        uniqueDisclaimers,
        uniqueDisclaimersArr,
      });

      if (uniqueDisclaimersArr.length <= 0) return null;

      return uniqueDisclaimersArr;
    },
    /**
     * Checks the products main collection then finds if a metafield
     * disclaimer is available in that collection
     * @param {FieldPortion | undefined} fieldPortion
     */
    getProductCollectionDisclaimer(fieldPortion) {
      if (!fieldPortion) return null;
      const { product } = fieldPortion;
      if (!product || !product.collections) return null;

      const metaFieldParentCollectionId = this.getMetafieldValueByName(
        product.metafields,
        "parent_collection"
      );

      if (!metaFieldParentCollectionId) return null;

      /**
       * @type {CollectionNode | null}
       */
      const collection = product.collections.nodes.find(
        (collection) => collection.id === metaFieldParentCollectionId
      );

      if (!collection) return null;

      const collectionDisclaimer = this.getMetafieldValueByName(
        collection.metafields,
        "disclaimer_note"
      );
      if (!collectionDisclaimer) return null;

      return collectionDisclaimer;
    },
    /**
     *
     * @param {ProductNode | undefined} product
     */
    productTitle(product) {
      if (!product) return nullLog({ msg: "productTitle" });
      const calculatorProductTitle = this.getMetafieldValueByName(
        product.metafields,
        "calculator_title"
      );
      return calculatorProductTitle ? calculatorProductTitle : product.title;
    },
    numberOfPreCartProducts() {
      /**
       * @type {FieldPortion[]}
       */
      const portions = [
        this.fieldPortion1,
        this.fieldPortion2,
        this.fieldPortion3,
      ];

      const portionsInPrecart = portions.filter(
        (portion) => portion.addPortion && portion.inPreCart
      );

      return portionsInPrecart.length;
    },
    /**
     * Resets all variables
     */
    reset() {
      this.kcal_difference = 0;
      this.kcal_per_day = 0;
      this.currentStep = 1;
      this.weightUnit = "kg";
      this.pet_kind = "dog";
      this.pet_kind_pural = "dogs";
      this.pet_kind_possessive = "dog's";
      this.pet_baby = "Puppy";
      this.pet_stage = null;
      this.pet_name = null;
      this.totalBowlPercentage = 0;
      this.fieldPetWeight = null;
      this.fieldPetKind = "dog";
      this.fieldPetName = "";
      this.fieldPetStage = { stageId: null, text: null };
      this.fieldPetActivity = { id: null, text: null };
      this.userChangedPercentage = false;
      this.percentageErrorText = null;
      this.numberOfServings = 1;

      /***
       * @type {FieldPortion}
       * */
      this.fieldPortion1 = {
        ...fieldPortionDefaults,
        position: 1,
        percentage: 70,
      };
      this.fieldPortion2 = {
        ...fieldPortionDefaults,
        position: 2,
        percentage: 20,
      };
      this.fieldPortion3 = {
        ...fieldPortionDefaults,
        position: 3,
        percentage: 10,
      };
      this.fieldPortion4 = {
        ...fieldPortionDefaults,
        addPortion: true,
        percentage: 0,
        position: 4,
      };
      this.calculatorProducts = undefined;
      this.productsPortion1 = undefined;
      this.productsPortion2 = undefined;
      this.productsPortion3 = undefined;
      this.validationSteps = { step1: false, step2: false, step3: false };
      this.loadingCart = false;
    },
    /**
     * @param {number[]} arr
     */
    sumArray(arr) {
      return arr.reduce((acc, curr) => {
        if (typeof curr === "number" && !isNaN(curr)) {
          return acc + curr;
        } else {
          console.error(
            `Array contains a non-number value "${curr}". Skipping...`
          );
          return acc;
        }
      }, 0);
    },
    calculateBowlCost() {
      /**
       * @type {FieldPortion[]}
       */
      const fieldPortions = [
        this.fieldPortion1,
        this.fieldPortion2,
        this.fieldPortion3,
      ];

      const preCartPortions = fieldPortions.filter(
        (portion) => portion.addPortion && portion.inPreCart
      );
      if (preCartPortions.length <= 0) return [0];

      const costPerRequiredGramPerProduct = preCartPortions.map((portion) => {
        if (!portion.selectedVariant) {
          return 0;
        }
        const requiredGrams = this.getRequiredGramsByKcals(portion);
        const variantCost = parseFloat(portion.selectedVariant.price.amount);
        const variantNetWeightMetafield = this.getMetafield(
          portion.selectedVariant.metafields,
          "net_weight_grams"
        );
        const quantityGroupValue = this.getMetafieldValueByName(
          portion.selectedVariant.metafields,
          "quantity_group"
        );

        const quantityGroup = () => {
          if (!quantityGroupValue) return 1;
          if (parseFloat(quantityGroupValue) === NaN) return 1;
          return parseFloat(quantityGroupValue);
        };

        const variantNetWeight =
          variantNetWeightMetafield && variantNetWeightMetafield.value
            ? parseFloat(variantNetWeightMetafield.value)
            : 0;

        const costOfMealPerRequiredGrams =
          (variantCost * requiredGrams) / (variantNetWeight * quantityGroup());

        return parseFloat(costOfMealPerRequiredGrams.toFixed(10));
      });

      return this.sumArray(costPerRequiredGramPerProduct).toFixed(2);
    },
    /**
     * Takes the required kcal from a product and calculates how
     * many grams are required for the product
     * Rule:
     * Grams = Calories needed / Calories per gram
     * @param {FieldPortion} portion
     * @returns {number} Kcals in Grams
     **/
    getRequiredGramsByKcals(portion) {
      const productKcalPerGram = this.getMetafield(
        portion.product.metafields,
        "kcal_per_gram"
      );

      if (!productKcalPerGram) return 0;

      const kcalForThisPercentedProduct =
        (portion.percentage / 100) * this.kcal_per_day;

      return kcalForThisPercentedProduct / parseFloat(productKcalPerGram.value);
    },
    /**
     * Takes the glogal id string add returns the id number
     * @param {string} id eg  "gid://shopify/ProductVariant/44131742548271"
     * @returns {number} 44131742548271
     */
    getIdFromGlobalUniqueId(id) {
      const parts = id.split("/");
      const lastItem = parts[parts.length - 1];
      return parseInt(lastItem);
    },
    addToCart() {
      if (this.loadingCart) {
        return null;
      }

      this.loadingCart = true;
      /**
       * @type {FieldPortion[]}
       */
      const portions = [
        this.fieldPortion1,
        this.fieldPortion2,
        this.fieldPortion3,
      ];

      const validPortions = portions.filter(
        (portion) => portion.addPortion && portion.inPreCart
      );
      if (validPortions.length <= 0) return null;

      const productsForCart = validPortions.map((validPortion) => ({
        id: this.getIdFromGlobalUniqueId(validPortion.selectedVariant.id),
        quantity: 1,
      }));

      const cart = document.querySelector("cart-drawer");

      const cartItems = {
        items: productsForCart,
        sections: cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };

      cart.setActiveElement(document.activeElement);

      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cartItems),
      })
        .then(async (response) => {
          const responseJson = await response.json();
          if (responseJson.status == 404) {
            this.loadingCart = false;
            alert("Oops something went wrong. Please try again later");
          }
          cart.renderContents(responseJson);
          this.loadingCart = false;
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    },
    calculateSubTotal() {
      const fieldPortions = [
        this.fieldPortion1,
        this.fieldPortion2,
        this.fieldPortion3,
      ];

      const subTotal = fieldPortions.reduce((total, fieldPortion) => {
        if (
          fieldPortion.addPortion &&
          fieldPortion.inPreCart &&
          fieldPortion.selectedVariant
        ) {
          total += parseFloat(fieldPortion.selectedVariant.price.amount);
        }
        return total;
      }, 0);

      return subTotal.toFixed(2);
    },
    updatePortionPostionNumber() {
      const portions = [
        this.fieldPortion1,
        this.fieldPortion2,
        this.fieldPortion3,
        this.fieldPortion4,
      ];
      const activePortions = portions.filter((portion) => portion.addPortion);

      // Update positions for active portions
      activePortions.forEach((portion, index) => {
        portion.position = index + 1;
      });

      // Update positions for inactive portions
      portions.forEach((portion) => {
        if (!portion.addPortion) {
          const availablePositions = activePortions.map(
            (portion) => portion.position
          );
          const newPosition = this.findAvailablePosition(availablePositions);
          portion.position = newPosition;
        }
      });
    },
    findAvailablePosition(occupiedPositions) {
      for (let i = 1; i <= 4; i++) {
        if (!occupiedPositions.includes(i)) {
          return i;
        }
      }
      // This should not be reached if there's at least one available position
      return null;
    },
    /**
     * @param {ProductNode | number} productOrPortionNumber
     */
    isCan(productOrPortionNumber) {
      /**
       * @type {FieldPortion | false}
       */
      const fieldPortion =
        !isNaN(productOrPortionNumber) &&
        this[`fieldPortion${productOrPortionNumber}`];

      /**
       * @type {ProductNode | false}
       */
      const product = isNaN(productOrPortionNumber) && productOrPortionNumber;

      const productNode = fieldPortion ? fieldPortion.product : product;

      if (productNode) {
        return productNode.productType.toLowerCase().includes("canned");
      }

      return false;
    },
    calculateCupsAndCans(fieldPortionNumber) {
      let cups = 0;
      let cans = 0;
      let requiredGrams = 0;
      let requiredOunces = 0;

      /**
       * @type {FieldPortion}
       */
      const fieldPortion = this[`fieldPortion${fieldPortionNumber}`];
      if (!fieldPortion) return { cans, cups, requiredGrams, requiredOunces };
      if (!fieldPortion.selectedVariant)
        return { cans, cups, requiredGrams, requiredOunces };

      const productKcalPerGram = this.getMetafield(
        fieldPortion.product.metafields,
        "kcal_per_gram"
      );

      const productGramsPerCup = this.getMetafield(
        fieldPortion.product.metafields,
        "gram_per_cup"
      );

      const selectedVariantNetWeight =
        fieldPortion.selectedVariant &&
        this.getMetafield(
          fieldPortion.selectedVariant.metafields,
          "net_weight_grams"
        );

      if (!selectedVariantNetWeight) {
        nullLog({
          msg: "calculateCupsAndCans",
          object: selectedVariantNetWeight,
        });
        return { cans, cups, requiredGrams, requiredOunces };
      }

      /**
       * @todo replace with getRequiredGramsByKcals()
       * Takes the required kcal from a product and calculates how
       * many grams are required for the product
       * Rule:
       * Grams = Calories needed / Calories per gram
       * @returns {number} Kcals in Grams
       **/
      const requiredKcalsForProductInGrams = () => {
        if (!productKcalPerGram) return 0;

        const kcalForThisPercentedProduct =
          (fieldPortion.percentage / 100) * this.kcal_per_day;

        const requiredGrams =
          kcalForThisPercentedProduct / parseFloat(productKcalPerGram.value);

        return requiredGrams;
      };

      requiredGrams = requiredKcalsForProductInGrams().toFixed(1);
      requiredOunces = (requiredKcalsForProductInGrams() * 0.03527396).toFixed(
        1
      );

      const calculateCups =
        productGramsPerCup &&
        requiredKcalsForProductInGrams() / parseFloat(productGramsPerCup.value);

      const calculateCans =
        requiredKcalsForProductInGrams() /
        parseFloat(selectedVariantNetWeight.value);

      cups = calculateCups ? calculateCups.toFixed(1) : 0;
      cans = calculateCans.toFixed(1);

      return { cans, cups, requiredGrams, requiredOunces };
    },

    /**
     *
     * @param {Metafield[]} metafields
     * @param {string} key
     * @returns
     */
    getMetafieldValueByName(metafields, key) {
      const foundMetafield = metafields.find(
        (metafield) =>
          metafield && metafield.hasOwnProperty("key") && metafield.key == key
      );
      return foundMetafield && foundMetafield.value;
    },

    /**
     *
     * @param {Metafield[]} metafields
     * @param {string} key
     * @returns {Metafield | undefined}
     */
    getMetafield(metafields, key) {
      /**
       * @type {Metafield | undefined}
       */
      const foundMetafield = metafields.find(
        (metafield) =>
          metafield && metafield.hasOwnProperty("key") && metafield.key == key
      );
      return foundMetafield;
    },
    userUpdatePercentagePortion() {
      const portion1Percentage =
        this.fieldPortion1.addPortion && this.fieldPortion1.percentage
          ? this.fieldPortion1.percentage
          : 0;
      const portion2Percentage =
        this.fieldPortion2.addPortion && this.fieldPortion2.percentage
          ? this.fieldPortion2.percentage
          : 0;
      const portion3Percentage =
        this.fieldPortion3.addPortion && this.fieldPortion3.percentage
          ? this.fieldPortion3.percentage
          : 0;

      const total =
        parseInt(portion1Percentage) +
        parseInt(portion2Percentage) +
        parseInt(portion3Percentage);

      if (total >= 100) {
        this.fieldPortion4.addPortion = false;
      } else {
        this.fieldPortion4.addPortion = true;
      }
      this.totalBowlPercentage = total;
      const percentageDifference = 100 - total;
      this.kcal_difference =
        (percentageDifference / 100) *
        parseInt(!isNaN(this.kcal_per_day) ? this.kcal_per_day : 0);

      this.updatePortionPostionNumber();
      this.validate();
    },
    initStep3() {
      this.calculateBowlCost();
    },
    async initStep2() {
      if (this.calculatorProducts) return null;
      this.calculatorProducts = await this.getProducts();
      const { portion1Products, portion2Products, portion3Products } =
        organiseProducts(this.calculatorProducts);

      this.productsPortion1 = portion1Products;
      this.productsPortion2 = portion2Products;
      this.productsPortion3 = portion3Products;

      const calPerDay = calculatePetsRequiredEnergy({
        weight: this.fieldPetWeight,
        activity: this.fieldPetActivity,
        petPower: this.petPower,
        weightUnit: this.weightUnit,
      });

      this.kcal_per_day = calPerDay;
    },

    /**
     * Updates/Add selected option value in fieldPortion${ number }
     * Expects $portion.product to have product data
     * @param {string} name
     * @param {string} selectedValue
     * @param {number} fieldPortionNumber
     * @returns void
     */
    updatePortionSelectedOption(name, selectedValue, fieldPortionNumber) {
      /**
       * @type {FieldPortion}
       */
      const fieldPortion = this["fieldPortion" + fieldPortionNumber];

      /**
       * @param {[SelectedOptions]} selectedOptions
       */
      const addNewVariant = (selectedOptions) => {
        if (!selectedOptions) return;

        const filterVariantWithMatchingOptions =
          fieldPortion.product.variants.nodes.filter((variantNode) =>
            haveSameOptions(variantNode.selectedOptions, selectedOptions)
          );

        const foundMatchedVariant =
          filterVariantWithMatchingOptions.length > 0
            ? filterVariantWithMatchingOptions[0]
            : false;

        if (!foundMatchedVariant) return;

        fieldPortion.selectedVariant = foundMatchedVariant;
        return true;
      };

      // there are no selectedOptions so we'll add
      if (!Array.isArray(fieldPortion.selectedOptions)) {
        fieldPortion.selectedOptions = [{ name, value: selectedValue }];
        addNewVariant(fieldPortion.selectedOptions);
        this.validate();
        return;
      }

      const optionNameFound = fieldPortion.selectedOptions.findIndex(
        (selectedOption) => selectedOption.name == name
      );

      // there is an optionName found so we'll update it
      if (optionNameFound !== -1) {
        fieldPortion.selectedOptions[optionNameFound].value = selectedValue;
        addNewVariant(fieldPortion.selectedOptions);
        this.validate();
        return;
      }

      fieldPortion.selectedOptions.push({ name, value: selectedValue });
      addNewVariant(fieldPortion.selectedOptions);
      this.validate();
      return;
    },

    getSelectedOption(name, fieldPortionNumber) {
      /**
       * @type {FieldPortion}
       */
      const fieldPortion = this["fieldPortion" + fieldPortionNumber];

      const thereIsAselectedOption = () => {
        if (Array.isArray(fieldPortion.selectedOptions)) {
          if (fieldPortion.selectedOptions.length > 0) {
            return true;
          }
        }
        return false;
      };

      if (!thereIsAselectedOption()) return null;

      const foundOption = fieldPortion.selectedOptions.filter(
        (option) => option.name == name
      );

      if (foundOption.length > 0) return foundOption[0];
    },

    /*
      If single use default variant
    */
    /**
     *
     * @param {ProductNode} product
     */
    productIsSingle(product) {
      if (!product) return false;
      const { options } = product;
      return options.some(
        (option) =>
          option.name === "Title" && option.values.includes("Default Title")
      );
    },
    /**
     *
     * @param {number} overwriteStep
     */
    validate(overwriteStep = null) {
      const currentStep = overwriteStep ?? this.currentStep;
      if (currentStep === 1) {
        this.validateStep1();
      }
      if (currentStep === 2) {
        this.validateStep2();
      }
    },
    validateStep1() {
      if (!this.controlsStep1) return null;
      const step1NextButton = this.controlsStep1.querySelector(
        '[data-direction="next"]'
      );
      const step1BackButton = this.controlsStep1.querySelector(
        '[data-direction="back"]'
      );

      if (!step1NextButton || !step1BackButton) return null;

      if (
        this.fieldPetActivity.id &&
        this.fieldPetWeight &&
        this.fieldPetStage &&
        this.fieldPetStage.stageId
      ) {
        this.validationSteps.step1 = true;
        step1NextButton.classList.remove("disable");
        return;
      }
      step1NextButton.classList.add("disable");
      return;
    },

    validateStep2() {
      if (!this.controlsStep2) return null;
      const step2NextButton = this.controlsStep2.querySelector(
        '[data-direction="next"]'
      );
      const step2BackButton = this.controlsStep2.querySelector(
        '[data-direction="back"]'
      );

      if (!step2NextButton || !step2BackButton) return null;

      if (this.totalBowlPercentage > 100) {
        step2NextButton.classList.add("disable");
        return;
      }
      /**
       * @type {[FieldPortion]}
       */
      const allPortions = [
        this.fieldPortion1,
        this.fieldPortion2,
        // this.fieldPortion3,
      ];

      const atLeast1PortionIsOpen =
        this.fieldPortion1.addPortion ||
        this.fieldPortion2.addPortion ||
        this.fieldPortion3.addPortion;

      if (!atLeast1PortionIsOpen) return null;

      const openedPortions = allPortions.filter(
        (portion) => portion.addPortion
      );

      const validOpendPortions = openedPortions.filter((portion) => {
        if (!portion.product) return false;
        if (this.productIsSingle(portion.product) && portion.percentage)
          return true;
        if (!Array.isArray(portion.selectedOptions) && !portion.percentage)
          return false;

        const bothOptionsAreArrays =
          Array.isArray(portion.selectedOptions) &&
          Array.isArray(portion.product.options);

        if (
          bothOptionsAreArrays &&
          portion.product.options.length == portion.selectedOptions.length &&
          portion.percentage
        )
          return true;
      });

      if (openedPortions.length === validOpendPortions.length) {
        step2NextButton.classList.remove("disable");
        return;
      }

      step2NextButton.classList.add("disable");
      return;
    },
    /**
     * Adds product object to fieldPortion and
     * adds selectedVariant if the product is a single product
     * @param {ProductNode} product
     * @param {string} fieldPortionName
     * @returns {void}
     */
    updatePortion(product, fieldPortionName) {
      if (!this[fieldPortionName] || !product) return null;

      const defaultVariant = this.hasDefaultVariant(product);
      const defaultVariantClean =
        defaultVariant && JSON.parse(JSON.stringify(defaultVariant));

      if (defaultVariantClean) {
        this[fieldPortionName].selectedVariant = defaultVariantClean;
        this[fieldPortionName].selectedOptions = JSON.parse(
          JSON.stringify(defaultVariantClean.selectedOptions)
        );
      }

      if (!defaultVariantClean) {
        this[fieldPortionName].selectedOptions = null;
        this[fieldPortionName].selectedVariant = null;
      }

      this[fieldPortionName].product = product;

      if (this.productIsSingle(product)) {
        this[fieldPortionName].selectedVariant = product.variants.nodes[0];
        this.validate();
        return;
      }

      this.validate();
    },

    /**
     * Checks all variants of a product to see if any variant has a
     * metafield of default_variant value is true and retuns the variant
     * @param {ProductNode} product
     */
    hasDefaultVariant(product) {
      if (this.productIsSingle(product)) return null;

      const { variants } = product;
      if (variants.nodes.length <= 0) return null;

      /**
       * @type {ProductVariant[]}
       */
      const variantsWithDefaultMetafield = variants.nodes.filter((variant) => {
        const defaultVariant = this.getMetafield(
          variant.metafields,
          "default_variant"
        );

        if (defaultVariant && defaultVariant.value === "true") return true;
        return false;
      });

      if (variantsWithDefaultMetafield.length <= 0) return null;
      return variantsWithDefaultMetafield[0];
    },

    toggleDropdownField(e, updateInputWithLi) {
      if (!e) return null;
      const allDropdowns = document.querySelectorAll(".dropdownField");
      const closestDropdownField = e.target.closest(".dropdownField");
      if (!closestDropdownField) return null;

      if (e.target.tagName === "LI" && updateInputWithLi) {
        const inputDivText = closestDropdownField.querySelector(".inputDiv p");
        if (inputDivText) {
          inputDivText.innerHTML = e.target.innerHTML;
        }
      }

      if (closestDropdownField.dataset.active == "false") {
        allDropdowns.forEach((dropdown) =>
          dropdown.setAttribute("data-active", "false")
        );
        closestDropdownField.setAttribute("data-active", "true");
        return;
      }
      closestDropdownField.setAttribute("data-active", "false");
    },
    focusInput(e) {
      const closestInput =
        e.target.closest("input") ||
        e.target.querySelector("input") ||
        e.target;
      if (closestInput) {
        closestInput.focus();
      }
    },
    updateInputWidth(e) {
      if (!e) return null;
      const { target } = e;
      target.style.width = `${target.value.length + 1}ch`;
    },

    /**
     * Applies default percentages depending on the number
     * of portions closed and opened
     */
    doDefaultBowlPercentages() {
      if (this.userChangedPercentage) return null;
      const numberOfPortionsOpen =
        (this.fieldPortion1.addPortion ? 1 : 0) +
        (this.fieldPortion2.addPortion ? 1 : 0) +
        (this.fieldPortion3.addPortion ? 1 : 0);

      const makePercentages = (percentages = [0, 0, 0, 0]) => {
        this.fieldPortion1.percentage = percentages[0];
        this.fieldPortion2.percentage = percentages[1];
        this.fieldPortion3.percentage = percentages[2];
        this.fieldPortion4.percentage = percentages[3];
      };

      if (numberOfPortionsOpen === 0) {
        makePercentages([0, 0, 0, 0]);
      }

      if (numberOfPortionsOpen === 1) {
        if (this.fieldPortion1.addPortion) {
          makePercentages([100, 0, 0, 0]);
        }
        if (this.fieldPortion2.addPortion) {
          makePercentages([0, 25, 0, 75]);
        }
        if (this.fieldPortion3.addPortion) {
          makePercentages([0, 0, 10, 90]);
        }
      }

      if (numberOfPortionsOpen === 2) {
        if (this.fieldPortion1.addPortion && this.fieldPortion2.addPortion) {
          makePercentages([75, 25, 0, 0]);
        }
        if (this.fieldPortion1.addPortion && this.fieldPortion3.addPortion) {
          makePercentages([90, 0, 10, 0]);
        }
        if (this.fieldPortion2.addPortion && this.fieldPortion3.addPortion) {
          makePercentages([0, 25, 10, 65]);
        }
      }

      if (numberOfPortionsOpen === 3) {
        makePercentages([70, 20, 10, 0]);
      }
    },
    /**
     * Toggle the portion based on a click event.
     * @param {MouseEvent} event - The click event.
     * @param {string} portionFieldName - The portion field name eg. fieldPortion1
     */
    togglePortion(event, portionFieldName) {
      if (!event) return null;
      const { currentTarget } = event;

      const clickedPortion = currentTarget.closest(".addPortion");
      if (!clickedPortion) return null;
      if (clickedPortion.dataset.active == "false") {
        clickedPortion.setAttribute("data-active", "true");
        if (this[portionFieldName]) {
          this[portionFieldName] = {
            ...this[portionFieldName],
            addPortion: true,
          };
        }
        this.validate();
        this.doDefaultBowlPercentages();
        return;
      }

      if (clickedPortion.dataset.active == "true") {
        clickedPortion.setAttribute("data-active", "false");
        this[portionFieldName] = {
          ...this[portionFieldName],
          addPortion: false,
        };
        this.validate();
        this.doDefaultBowlPercentages();
        return;
      }

      this.doDefaultBowlPercentages();
      return;
    },

    /**
     * Toggle the portion based on a click event.
     * @param {MouseEvent} event - The click event.
     */
    openPortion(event) {
      const { currentTarget } = event;
      currentTarget.setAttribute("data-active", "true");

      const nextSibling = currentTarget.nextElementSibling;
      const prevSibling = currentTarget.prevSibling;

      if (nextSibling) {
        if (nextSibling.dataset.active == "false") {
          nextSibling.setAttribute("data-has-top-attached", "false");
        }
      }

      if (prevSibling) {
        if (prevSibling.dataset.active == "false") {
          prevSibling.setAttribute("data-has-bottom-attached", "false");
        }
      }
    },

    /**
     *
     * @param {PetActivity} petActivity
     */
    updatePetActivity(petActivity) {
      if (!petActivity) return null;

      this.fieldPetActivity = petActivity;
    },

    /**
     * @typedef {'back' | 'next' | undefined} Direction
     * @param {Direction} direction The direction of the control to trigger
     */
    changeStep(direction) {
      if (direction === "next") {
        this.currentStep = this.currentStep + 1;
      }

      if (direction === "back") {
        this.currentStep = this.currentStep - 1;
      }
      return;
    },
    prevStep() {
      if (this.currentStep === 1) return null;
      this.currentStep = this.currentStep - 1;
    },
    nextStep() {
      this.currentStep = this.currentStep + 1;
      this.validateStep2();
    },

    log() {
      console.log({
        kcal_per_day: this.kcal_per_day,
        kcal_difference: this.kcal_difference,
        controlsStep1: this.controlsStep1,
        controlsStep2: this.controlsStep2,
        controlsStep3: this.controlsStep3,
        totalBowlPercentage: this.totalBowlPercentage,
        userChangedPercentage: this.userChangedPercentage,
        fieldPortion4: this.fieldPortion4,
        portion1: this.productsPortion1,
        aaa: this.calculatorProducts,
        values: this.values,
        translationData: this.translationData,
        fieldPetKind: this.fieldPetKind,
        fieldPetName: this.fieldPetName,
        fieldPetWeight: this.fieldPetWeight,
        fieldPetStage: this.fieldPetStage,
        fieldPetActivity: this.fieldPetActivity,
        tActivityDogAdultArr: this.tActivityDogAdultArr,
        tPetStageArr: this.tPetStageArr,
        fieldPortion1: this.fieldPortion1,
        fieldPortion2: this.fieldPortion2,
        fieldPortion3: this.fieldPortion3,
        weightUnit: this.weightUnit,
      });
    },

    updatePetStage(stageId, stageText) {
      if (stageId == null || stageText == null) return null;
      this.fieldPetStage = {
        stageId,
        stageText,
      };
    },

    getShopUrl() {
      if (!this.fcInner) return null;
      return this.fcInner.dataset.shopUrl;
    },
    /**
     * Gets all products by pet kind and
     * returns null or an array of product nodes: https://shopify.dev/docs/api/storefront/unstable/objects/ProductEdge
     * The specific fields return are assigned to `query`
     * @returns {Promise<null | ProductEdge[]>}
     */
    async getProducts() {
      if (!this.getShopUrl() || !this.calculatorSettings) return null;
      if (!this.calculatorSettings.accessToken) return null;

      const apiUrl = `${this.getShopUrl()}/api/2024-01/graphql.json`;
      const query = `
      query getProducts($identifiers: [HasMetafieldsIdentifier!]!,$query:String, $mIdentifiers: [HasMetafieldsIdentifier!]!, $cIdentifiers: [HasMetafieldsIdentifier!]!) {
        products(first: 100, query: $query) {
          edges {
            node {
              availableForSale
              id
              title
              description
              handle
              tags
              productType
              collections(first:20){
                nodes{
                  title
                  id
                  metafields(identifiers: $cIdentifiers){
                    id
                    namespace
                    value
                    key
                  }
                }
              }
              featuredImage{
                altText
                url(transform: {maxWidth: 100})
                width
                height
              }
              options(first:50){
                id
                name
                values
              }
              metafields(identifiers: $identifiers){
                id
                key
                value
                type
              }
              variants(first: 100) {
                nodes {
                  availableForSale
                  currentlyNotInStock
                  title
                  id
                  metafields(identifiers: $mIdentifiers){
                    id
                    key
                    value
                    type
                  }
                  price {
                    amount
                    currencyCode
                  }
                  selectedOptions{
                    name
                    value
                  }
                  image { 
                    altText
                    url(transform: {maxWidth: 100})
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    `;
      const variables = {
        mIdentifiers: [
          {
            key: "net_weight_grams",
            namespace: "custom",
          },
          {
            key: "quantity_group",
            namespace: "custom",
          },
          {
            key: "default_variant",
            namespace: "custom",
          },
        ],
        cIdentifiers: [
          {
            key: "disclaimer_note",
            namespace: "custom",
          },
        ],
        identifiers: [
          {
            key: "calculator_puppy_disclaimer_note",
            namespace: "custom",
          },
          {
            key: "product_display_title",
            namespace: "custom",
          },
          {
            key: "gram_per_cup",
            namespace: "custom",
          },
          {
            key: "kcal_per_gram",
            namespace: "custom",
          },
          {
            key: "calculator_title",
            namespace: "custom",
          },
          {
            key: "calculator_portion_1",
            namespace: "custom",
          },
          {
            key: "calculator_portion_2",
            namespace: "custom",
          },
          {
            key: "calculator_portion_3",
            namespace: "custom",
          },
          {
            key: "parent_collection",
            namespace: "custom",
          },
        ],
        query: `tag:${this.pet_kind}`,
      };

      let products = null;

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token":
              this.calculatorSettings.accessToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.data.products.edges.length > 1) {
          products = data.data.products.edges;
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        return products;
      }

      return products;
    },

    /**
     *
     * @param {string} petKind 'dog' | 'cat'
     */
    updatePetKind(petKind = "dog") {
      if (petKind === "cat") {
        this.pet_kind =
          this.translationData.feeding_calculator.general.cat.toLowerCase();
        this.pet_kind_pural =
          this.translationData.feeding_calculator.general.cat_plural.toLowerCase();
        this.pet_kind_possessive =
          this.translationData.feeding_calculator.general.cat_possessive.toLowerCase();
        this.pet_baby =
          this.translationData.feeding_calculator.general.cat_baby;
        return;
      }
      this.pet_kind =
        this.translationData.feeding_calculator.general.dog.toLowerCase();
      this.pet_kind_pural =
        this.translationData.feeding_calculator.general.dog_plural.toLowerCase();
      this.pet_kind_possessive =
        this.translationData.feeding_calculator.general.dog_possessive.toLowerCase();
      this.pet_baby = this.translationData.feeding_calculator.general.dog_baby;
      return;
    },

    filterNonNumeric(variableToUpdate) {
      this[variableToUpdate] = this[variableToUpdate].replace(/[^0-9]/g, "");
    },
  },
});

app.mount("#ziwiFeedingCalculator");

/*
  HELPERS
*/

/**
 * @typedef {Object} TranslationData
 * @property {null | object} all
 * @property {null | object} petStage
 * @property {null | [PetActivity] } activityDogBaby
 * @property {null | [PetActivity] } activityDogAdult
 * @property {null | [PetActivity] } activityDogSenior
 * @property {null | [PetActivity] } activityCatBaby
 * @property {null | [PetActivity] } activityCatAdult
 * @property {null | [PetActivity]} activityCatSenior
 */

/**
 * Gets all translation data from the shopify locales
 * and creates an object adding in the hard defined energy levels
 *
 * @returns {TranslationData} An object with properties for various animal stages, initially set to null.
 */
function getTranslationData() {
  /***
   * @type {TranslationData}
   * */
  const data = {
    all: null,
    petStage: null,
    activityDogBaby: null,
    activityDogAdult: null,
    activityDogSenior: null,
    activityCatBaby: null,
    activityCatAdult: null,
    activityCatSenior: null,
  };

  const fcTranslations = document.getElementById("fcTranslations");
  if (fcTranslations == null) return { ...data };

  data.all =
    JSON.parse(fcTranslations.innerText || fcTranslations.textContent) ?? null;

  if (!data.all) return { ...data };

  data.activityDogBaby = [
    {
      id: "pet_activity_dog_puppy1",
      text: data.all.feeding_calculator.step1.pet_activity_dog_puppy1,
      energy: 260,
      multiplier: 0,
    },
    {
      id: "pet_activity_dog_puppy2",
      text: data.all.feeding_calculator.step1.pet_activity_dog_puppy2,
      energy: 230,
      multiplier: 0,
    },
    {
      id: "pet_activity_dog_puppy3",
      text: data.all.feeding_calculator.step1.pet_activity_dog_puppy3,
      energy: 190,
      multiplier: 0,
    },
    {
      id: "pet_activity_dog_puppy4",
      text: data.all.feeding_calculator.step1.pet_activity_dog_puppy4,
      energy: 150,
      multiplier: 0,
    },
  ];

  data.activityDogAdult = [
    {
      id: "pet_activity_dog_adult_urban",
      text: data.all.feeding_calculator.step1.pet_activity_dog_adult_urban,
      energy: 85,
      multiplier: 0,
    },
    {
      id: "pet_activity_dog_adult_adventurer",
      text: data.all.feeding_calculator.step1.pet_activity_dog_adult_adventurer,
      energy: 100,
      multiplier: 0,
    },
    {
      id: "pet_activity_dog_adult_more",
      text: data.all.feeding_calculator.step1.pet_activity_dog_adult_more,
      energy: 115,
      multiplier: 0,
    },
    {
      id: "pet_activity_dog_adult_highly",
      text: data.all.feeding_calculator.step1.pet_activity_dog_adult_highly,
      energy: 130,
      multiplier: 0,
    },
    {
      id: "pet_activity_dog_adult_pregnant",
      text: data.all.feeding_calculator.step1.pet_activity_dog_adult_pregnant,
      energy: 130,
      multiplier: 26,
    },
    {
      id: "pet_activity_dog_adult_lactating",
      text: data.all.feeding_calculator.step1.pet_activity_dog_adult_lactating,
      energy: 130,
      multiplier: 144,
    },
  ];

  data.activityDogSenior = [
    {
      id: "pet_activity_dog_senior_pregnant",
      text: data.all.feeding_calculator.step1.pet_activity_dog_senior_pregnant,
      energy: 130,
      multiplier: 26,
    },
    {
      id: "pet_activity_dog_senior_lactating",
      text: data.all.feeding_calculator.step1.pet_activity_dog_senior_lactating,
      energy: 130,
      multiplier: 144,
    },
    {
      id: "pet_activity_dog_senior_neither",
      text: data.all.feeding_calculator.step1.pet_activity_dog_senior_neither,
      energy: 85,
      multiplier: 0,
    },
  ];

  data.activityCatBaby = [
    {
      id: "pet_activity_cat_kitten1",
      text: data.all.feeding_calculator.step1.pet_activity_cat_kitten1,
      energy: 360,
      multiplier: 0,
    },
    {
      id: "pet_activity_cat_kitten2",
      text: data.all.feeding_calculator.step1.pet_activity_cat_kitten2,
      energy: 200,
      multiplier: 0,
    },
    {
      id: "pet_activity_cat_kitten3",
      text: data.all.feeding_calculator.step1.pet_activity_cat_kitten3,
      energy: 150,
      multiplier: 0,
    },
    {
      id: "pet_activity_cat_kitten4",
      text: data.all.feeding_calculator.step1.pet_activity_cat_kitten4,
      energy: 125,
      multiplier: 0,
    },
  ];

  data.activityCatAdult = [
    {
      id: "pet_activity_cat_adult_average",
      text: data.all.feeding_calculator.step1.pet_activity_cat_adult_average,
      energy: 100,
      multiplier: 0,
    },
    {
      id: "pet_activity_cat_adult_less_active",
      text: data.all.feeding_calculator.step1
        .pet_activity_cat_adult_less_active,
      energy: 80,
      multiplier: 0,
    },
    {
      id: "pet_activity_cat_adult_pregnant",
      text: data.all.feeding_calculator.step1.pet_activity_cat_adult_pregnant,
      energy: 165,
      multiplier: 0,
    },
    {
      id: "pet_activity_cat_adult_lactating",
      text: data.all.feeding_calculator.step1.pet_activity_cat_adult_lactating,
      energy: 100,
      multiplier: 84,
    },
  ];

  data.activityCatSenior = [
    {
      id: "pet_activity_cat_senior_pregnant",
      text: data.all.feeding_calculator.step1.pet_activity_cat_senior_pregnant,
      energy: 165,
      multiplier: 0,
    },
    {
      id: "pet_activity_cat_senior_lactating",
      text: data.all.feeding_calculator.step1.pet_activity_cat_senior_lactating,
      energy: 100,
      multiplier: 84,
    },
    {
      id: "pet_activity_cat_senior_neither",
      text: data.all.feeding_calculator.step1.pet_activity_cat_senior_neither,
      energy: 80,
      multiplier: 0,
    },
  ];

  data.petStage = [
    {
      id: "pet_stage_baby",
      text: data.all.feeding_calculator.step1.pet_stage_baby,
    },
    {
      id: "pet_stage_adult",
      text: data.all.feeding_calculator.step1.pet_stage_adult,
    },
    {
      id: "pet_stage_senior",
      text: data.all.feeding_calculator.step1.pet_stage_senior,
    },
  ];

  return { ...data };
}

function replacePlaceholders(template, values) {
  const regex = /\$\{\s*([^}\s]+)\s*\}/g;
  const replacedString = template.replace(regex, (match, key) => {
    return values[key] || match;
  });
  return replacedString;
}

// Create a global mixin to add the function to all components
function customMixin() {
  return {
    methods: {
      replacePlaceholders(template, values) {
        return replacePlaceholders(template, values);
      },
    },
  };
}

function getCalculatorSettings() {
  const calculatorSettingsElement =
    document.getElementById("calculatorSettings");

  if (!calculatorSettingsElement) return null;

  const calculatorSettingsObject =
    JSON.parse(
      calculatorSettingsElement.innerText ||
        calculatorSettingsElement.textContent
    ) ?? null;

  return calculatorSettingsObject;
}

/**
 * Organises products for a specific portion for step 2
 *
 * @param {ProductEdge[]} products - Array of nodes: https://shopify.dev/docs/api/storefront/unstable/objects/ProductEdge
 *
 * @returns {{
 *   portion1Products: ProductEdge[] | null,
 *   portion2Products: ProductEdge[] | null,
 *   portion3Products: ProductEdge[] | null
 * }} Returns an object containing arrays of products for each metafield portion number
 * If no products have the metafield for a portion, the corresponding property will be null
 */
function organiseProducts(products) {
  let portion1Products = [];
  let portion2Products = [];
  let portion3Products = [];

  const checkMetafield = (metafields, portion = 1) => {
    const allMetaFieldsNull = metafields.every(
      (metafield) => metafield === null
    );
    if (allMetaFieldsNull) return null;

    const hasCalculatorPortion = metafields.some(
      (metafield) =>
        metafield &&
        metafield.key === `calculator_portion_${portion}` &&
        metafield.value === "true"
    );
    if (!hasCalculatorPortion) return null;

    return true;
  };

  products.forEach((product) => {
    if (checkMetafield(product.node.metafields, 1)) {
      portion1Products.push(product);
    }

    if (checkMetafield(product.node.metafields, 2)) {
      portion2Products.push(product);
    }

    if (checkMetafield(product.node.metafields, 3)) {
      portion3Products.push(product);
    }
  });

  sortProductsOrder(["S", "A", "F", "C"], portion1Products);
  sortProductsOrder(["S", "A", "F", "C"], portion2Products);
  sortProductsOrder(["A", "F"], portion3Products);
  return {
    portion1Products,
    portion2Products,
    portion3Products,
  };
}

/**
 * Orders the list of products by the order of letters provided
 * @param {string[]} order
 * @param {ProductEdge} products
 */
function sortProductsOrder(order, products) {
  // Create a custom sorting function
  function customSort(a, b) {
    const aIndex = order.indexOf(a.node.title[0]);
    const bIndex = order.indexOf(b.node.title[0]);

    // If first character of a or b doesn't exist in the order, put it at the bottom
    if (aIndex === -1 && bIndex === -1) {
      return a.node.title.localeCompare(b.node.title); // Sort based on title if both don't match order
    } else if (aIndex === -1) {
      return 1; // Put a at the bottom
    } else if (bIndex === -1) {
      return -1; // Put b at the bottom
    }

    // If both have a valid index in the order, sort based on their indices
    if (aIndex < bIndex) {
      return -1;
    } else if (aIndex > bIndex) {
      return 1;
    } else {
      // If first characters are the same, compare the entire title
      return a.node.title.localeCompare(b.node.title);
    }
  }

  // Sort the products array using the custom sorting function
  return products.sort(customSort);
}

/**
 * @typedef {object} NullLog
 * @property {string} msg
 * @property {object} object
 *
 * @param {NullLog} data
 * @returns {null}
 */
function nullLog(data) {
  if (!isDevelopment) return null;
  const { msg, object } = data;
  if (!object) {
    console.log(`%cLog Error${msg && `: ${msg}`}`, `color:red`);
  }
  if (object) {
    console.log(`%cLog Error${msg && `: ${msg}`}`, `color:red`, object);
  }
  return null;
}

/**
 * Calculates the Pets daily kcal intake based on their age, activity etc.
 * @typedef {Object} CalculatePetsRequiredEnergy
 * @property {number | null} weight
 * @property {PetActivity | null} activity
 * @property {number | null} petPower
 * @property { 'kg' | 'lb'} weightUnit
 * @param {CalculatePetsRequiredEnergy} data
 */
function calculatePetsRequiredEnergy(data) {
  if (
    !data ||
    !data.weight ||
    !data.activity ||
    !data.activity.energy ||
    !data.activity.id ||
    !data.petPower
  )
    return nullLog({ msg: "calculatePetsRequiredEnergy()", object: data });

  const { weight, activity, petPower, weightUnit } = data;
  const { energy, multiplier } = activity;

  // if not kg then convert lbs to kg
  const weightByUnit = weightUnit === "kg" ? weight : weight * 0.453592;

  if (multiplier) {
    return Math.floor(
      energy * Math.pow(weightByUnit, petPower) + weightByUnit * multiplier
    );
  }

  return Math.floor(energy * Math.pow(weightByUnit, petPower));
}

/**
 * Check if arrays have same length,
 * then sorts the arrays based on the objects name
 * @param {[SelectedOptions]} array1
 * @param {[SelectedOptions]} array2
 * @return {boolean}
 * */
function haveSameOptions(array1, array2) {
  if (array1.length !== array2.length) {
    return false;
  }

  const sortedArray1 = array1.slice().sort(compareObjects);
  const sortedArray2 = array2.slice().sort(compareObjects);

  for (let i = 0; i < sortedArray1.length; i++) {
    if (
      sortedArray1[i].name !== sortedArray2[i].name ||
      sortedArray1[i].value !== sortedArray2[i].value
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Compare object to check if they match
 * @param {SelectedOption} obj1
 * @param {SelectedOption} obj2
 * @returns
 */
function compareObjects(obj1, obj2) {
  // Comparison function to sort objects by name
  if (obj1.name < obj2.name) {
    return -1;
  }
  if (obj1.name > obj2.name) {
    return 1;
  }
  // If names are equal, compare values
  if (obj1.value < obj2.value) {
    return -1;
  }
  if (obj1.value > obj2.value) {
    return 1;
  }
  return 0;
}
function isClickOutsideElement(element, event) {
  return !element.contains(event.target);
}

document.addEventListener("click", function (event) {
  const elements = document.querySelectorAll(".dropdownField");

  elements.forEach((dropdownField) => {
    if (dropdownField.dataset.active == "true") {
      if (isClickOutsideElement(dropdownField, event)) {
        dropdownField.setAttribute("data-active", "false");
      }
    }
  });
});

function autoScrollElementTop(element, offset = 150) {
  if (element != null) {
    const position = element.getBoundingClientRect();
    window.scroll({
      top: position.top + window.scrollY - offset,
      left: 0,
      behavior: "smooth",
    });
  }
}
